import makeStyles from '@mui/styles/makeStyles'
import Typography from '@mui/material/Typography'
import { Alert } from '@mui/material'
import BillboardAd from 'components/BillboardAd'
import CardShow from 'components/CardShow'
import Layout from 'components/Layout'
import { MoreShowsButton } from 'components/MoreShowsButton'
import BreadcrumbSeriesJsonLd from 'components/seo/BreadcrumbSeriesJsonLd'
import WebpageJsonLd from 'components/seo/WebpageJsonLd'
import SeriesDetails from 'components/SeriesDetails'
import ShowDetails from 'components/ShowDetails'
import SwiperShows from 'components/SwiperShows'
import { MORE_SHOWS_AMOUNT, SORT_DATE_TITLE_DESC, SORT_RANKING } from 'lib/constants'
import { getSeries } from 'lib/series'
import { getShows } from 'lib/shows'
import { isJoynStation, isTVNowStation } from 'lib/stations'
import { caching, findLatestVideoForSeries, getImageURL } from 'lib/utils'
import Head from 'next/head'

const useStyles = makeStyles((theme) => ({
  current: {
    marginBottom: theme.spacing(7)
  },
  adContainer: {
    marginBottom: theme.spacing(3)
  },
  moreContent: {
    display: 'flex',
    flexWrap: 'wrap'
  },
  card: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(2)
  }
}))

export default function Series ({ series, current, shows, hasMore, newShows }) {
  const classes = useStyles()
  const title = `${series.name} verpasst? Ganze Folge kostenlos online sehen!`

  const description = (series) => {
    if (series.stations[0] && series.stations[0].name) {
      const station = series.stations[0]
      if (isTVNowStation(station.slug)) {
        return `${series.name} bei ${station.name} (TVNOW) verpasst? Kein Problem, hier gibt's die aktuellen Folgen als Video`
      } else if (isJoynStation(station.slug)) {
        return `${series.name} bei ${station.name} (Joyn) verpasst? Kein Problem, hier gibt's die aktuellen Folgen als Video`
      } else {
        return `${series.name} bei ${station.name} verpasst? Kein Problem, hier gibt's die aktuellen Folgen als Video`
      }
    } else {
      return `${series.name} verpasst? Kein Problem, hier gibt's die aktuellen Folgen als Video`
    }
  }

  const header = (
    <Head>
      <title>{title}</title>
      <meta name='description' content={description(series)} />
    </Head>
  )

  if (current) {
    return (
      <>
        {header}
        <WebpageJsonLd name={series.name} image={getImageURL(current.image, series.stations[0].slug)} />
        <BreadcrumbSeriesJsonLd series={series} />

        <Layout header={
          <SeriesDetails series={series} />
          }
        >
          <BillboardAd slot={process.env.NEXT_PUBLIC_ADSENSE_SLOT_SERIES_DETAILS} className={classes.adContainer} />
          <section className={classes.current}>
            <Typography component='h2' variant='h2' gutterBottom>Aktuelle Folge</Typography>
            <ShowDetails show={current} hideChips={series.description} headerTag='h2' />
          </section>
          {shows &&
            <section className={classes.more}>
              <Typography component='h2' variant='h2' gutterBottom>Weitere Folgen</Typography>
              <div className={classes.moreContent} data-test='more-videos'>
                {shows.map(({ _source }, index) => (
                  <div key={_source.id} className={classes.card}>
                    <CardShow props={_source} />
                  </div>
                ))}
              </div>
              {hasMore &&
                <MoreShowsButton series={series.name} />}
            </section>}
        </Layout>
      </>
    )
  } else {
    return (
      <>
        {header}
        <Layout header={
          <SeriesDetails series={series} />
          }
        >
          {
            !series.description &&
              <div>
                <Alert severity='warning'>Leider derzeit keine Videos verfügbar.</Alert>
                <p>
                  Die Sender stellen immer mehr Beiträge ins Netz. Leider ist nicht alles online verfügbar oder das Video
                  wurde wieder entfernt.
                </p>
                <SwiperShows title='Neu eingestellt' items={newShows} />
              </div>
          }
        </Layout>
      </>
    )
  }
}

export async function getServerSideProps ({ res, query }) {
  caching(res, process.env.CACHE_SHOW)

  const slug = query.show
  const seriesResult = await getSeries({ slug })

  if (seriesResult.hits.total.value === 0) {
    return { notFound: true }
  }

  const dbShows = await getShows({ series: slug, sort: SORT_DATE_TITLE_DESC })
  const total = dbShows.hits.total.value
  const series = seriesResult.hits.hits[0]._source

  let current = null
  let shows = null
  let newShows = null
  let hasMore = false

  if (total === 1) {
    current = dbShows.hits.hits[0]._source
  } else if (total > 1) {
    current = findLatestVideoForSeries(series, dbShows.hits.hits)._source
    shows = dbShows.hits.hits.filter(show => show._id !== current.id)
    if (total > (MORE_SHOWS_AMOUNT + 1)) {
      hasMore = true
    }
  } else {
    const newResult = await getShows({ sort: SORT_RANKING })
    newShows = newResult.hits.hits
  }

  return {
    props: {
      series,
      current,
      shows,
      hasMore,
      newShows
    }
  }
}
